import { useMutation } from '@apollo/client';
import React, { useCallback } from 'react';
import { Form } from 'react-final-form';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

import { InputField } from '../../../components/FinalForm/InputField';
import { SubmitButtonV3 } from '../../../components/FinalForm/SubmitButtonV3';
import { TextAreaField } from '../../../components/FinalForm/TextAreaField';
import { confirmationToast, failureToast } from '../../../components/Toast';
import { useConnectedRouter } from '../../../hooks/useConnectedRouter';
import { ReactComponent as InformationIcon } from '../../../icons/information.svg';
import { useAppDispatch } from '../../../redux/hooks';
import { isEmail, minLength } from '../../../utils/validation';
import { createOffenseMutation } from '../../api/offense';
import { updateTopic } from '../../ThreadMessage/topic-slice';

const messages = defineMessages({
  title: { defaultMessage: 'Report an offense', id: 'D48+K2' },
  emailPlaceholder: { defaultMessage: 'Your email', id: 'nONnTw' },
  contentPlaceholder: {
    defaultMessage: `Please specify the observed infraction here, for example: - Racism - Spam - Pedophilia - Pornography - Suicide - Call for murder - Respect for victims - Religious insult - Homophobia - Incitement to hate/violence/crime/theft`,
    id: 'yhym3p',
  },
  legalWarning: {
    defaultMessage:
      'NB: your IP address will be recorded with this report. Any abusive reporting may be sanctioned.',
    id: 'UeDhud',
  },
  success: { defaultMessage: 'Report sent', id: 'uIzt6m' },
  error: { defaultMessage: 'An error has occurred', id: 'h4hXDR' },
});

const contentValidation = minLength(4);

const FORM_SUBSCRIPTION = {};

export type ReportCommentProps = {
  id: string;
  object: 'comment' | 'topic';
  controller?: { hide: () => void };
  className?: string;
};

export const ReportComment: React.FC<ReportCommentProps> = ({ id, object, controller }) => {
  const { formatMessage } = useIntl();
  const [createOffense] = useMutation(createOffenseMutation);
  const dispatch = useAppDispatch();
  const router = useConnectedRouter();

  const handleUpdateState = useCallback(
    (creationDate: string) => {
      if (object === 'topic') {
        dispatch(updateTopic({ userReport: { createdAt: creationDate } }));
      } else {
        router.reload();
      }
    },
    [object, dispatch, router],
  );

  const renderForm = useCallback(
    ({ handleSubmit }) => (
      <form onSubmit={handleSubmit} className="flex flex-col space-y-4 mt-6">
        <InputField
          placeholder={formatMessage(messages.emailPlaceholder)}
          name="email"
          required
          validate={isEmail}
        />
        <TextAreaField
          placeholder={formatMessage(messages.contentPlaceholder)}
          name="content"
          required
          validate={contentValidation}
          rows={12}
        />
        <div className="pt-1 pb-3 flex items-center space-x-3">
          <InformationIcon className="w-5 h-5" />{' '}
          <em className="flex-1 text-gray-550">
            <FormattedMessage {...messages.legalWarning} />
          </em>
        </div>
        <SubmitButtonV3 variant="contained">Envoyer</SubmitButtonV3>
      </form>
    ),
    [formatMessage],
  );

  const submit = useCallback(
    async values => {
      try {
        const { data } = await createOffense({ variables: { ...values, id, object } });
        controller?.hide();
        confirmationToast(<FormattedMessage {...messages.success} />);
        handleUpdateState(data.createOffense.createdAt);
      } catch (error) {
        failureToast(<FormattedMessage {...messages.error} />);
      }
    },
    [createOffense, id, object, controller, handleUpdateState],
  );

  return (
    <div className="bg-white w-screen max-w-full">
      <div className="text-md font-bold">
        <FormattedMessage {...messages.title} />
      </div>
      <Form onSubmit={submit} subscription={FORM_SUBSCRIPTION} render={renderForm} />
    </div>
  );
};
